import React, { useState, useRef } from 'react';

export interface FileUploadProps {
  file?: File;
  onFileUpdate: (file: File | undefined) => void;
  accept?: string;
  placeholder?: string;
}

const UploadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 4v12M8 8l4-4 4 4" />
  </svg>
);

const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kb`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (ts: number): string => {
  const d = new Date(ts);
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
};

export const FileUpload = ({
  file,
  onFileUpdate,
  accept,
  placeholder = 'Drag & drop or click to browse',
}: FileUploadProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.currentTarget.files?.[0];
    if (f) onFileUpdate(f);
    e.currentTarget.value = '';
  };

  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(false); };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) onFileUpdate(f);
  };

  if (file) {
    return (
      <div className="file-loaded">
        <div>
          <div className="fname">{file.name}</div>
          <div className="fmeta">{formatDate(file.lastModified)} · {formatSize(file.size)}</div>
        </div>
        <button className="fclear" onClick={() => onFileUpdate(undefined)} title="Remove file" type="button">
          ✕
        </button>
      </div>
    );
  }

  return (
    <div
      className={`drop-zone${isDragging ? ' drag-over' : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={() => inputRef.current?.click()}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && inputRef.current?.click()}
    >
      <input
        ref={inputRef}
        type="file"
        onChange={handleFileChange}
        style={{ display: 'none' }}
        accept={accept}
      />
      <UploadIcon />
      <p>{placeholder}</p>
    </div>
  );
};
